import { graphql, Link } from "gatsby";
import Img from "gatsby-image";
import React from "react";
import CtaMini from "../components/CtaMini";
import Layout from "../components/Layout";
import userAvatar from "../images/female_avatar.svg";
import { DraftboxAuthor } from "../models/draftbox-author-description.model";
import Helmet from "react-helmet";

type AuthorsProps = {
  data: {
    allDraftboxAuthor: {
      edges: {
        node: Pick<
          DraftboxAuthor,
          "name" | "bio" | "count" | "profileImage" | "slug" | "socials"
        >;
      }[];
    };
    draftboxSettings: {
      identity: {
        siteName: string;
      };
    };
  };
};

const Authors: React.FC<AuthorsProps> = ({
  data: { allDraftboxAuthor, draftboxSettings },
}) => {
  return (
    <>
      <Helmet>
        <title>Authors | {draftboxSettings.identity.siteName}</title>
      </Helmet>
      <Layout>
        <div className="spacer my-6"></div>
        <h1 className="text-4xl font-sansBold text-center">
          {allDraftboxAuthor?.edges?.length > 0
            ? "Authors"
            : "No authors available"}
        </h1>
        <div className="spacer my-6"></div>
        <section className="px-4 container mx-auto">
          <div className="flex flex-wrap justify-center -mx-4">
            {allDraftboxAuthor.edges.map(({ node: author }, i) => {
              return (
                <div
                  key={i}
                  className="w-full lg:w-1/3 px-4 mb-8 cursor-pointer"
                >
                  <div className="h-full rounded shadow-md flex flex-col justify-between">
                    <div className="text-center p-4">
                      <Link to={`/author/${author.slug}`}>
                        {author.profileImage?.childImageSharp && (
                          <Img
                            className="w-32 h-32 mx-auto mb-4 rounded-full"
                            fluid={author.profileImage.childImageSharp.fluid}
                          />
                        )}
                        {!author.profileImage?.childImageSharp &&
                          author.profileImage && (
                            <img
                              className="h-32 w-32 mx-auto mb-4 rounded-full"
                              src={author.profileImage.publicURL}
                            />
                          )}
                        {!author.profileImage && (
                          <img
                            className="h-32 w-32 mx-auto mb-4 rounded-full"
                            src={userAvatar}
                            alt=""
                          />
                        )}
                        <h3 className="text-xl font-sansSemibold">
                          {author.name}
                        </h3>
                        {author.bio && (
                          <p className="mt-4 text-gray-700 leading-relaxed">
                            {author.bio}
                          </p>
                        )}
                      </Link>
                      {author.socials?.length > 0 && (
                        <div className="flex flex-wrap justify-center items-center space-x-6 mt-8">
                          {author.socials.map((social, index) => {
                            return (
                              <a
                                className="text-gray-700"
                                key={index}
                                rel="noreferrer noopener"
                                href={`${social.url}`}
                                target="_blank"
                                aria-label={social.label}
                              >
                                <i
                                  className={`${social.icon[0]} fa-${social.icon[1]} fa-lg`}
                                ></i>
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        <CtaMini />
      </Layout>
    </>
  );
};

export default Authors;

export const AuthorsQuery = graphql`
  query {
    allDraftboxAuthor(filter: { count: { posts: { gt: 0 } } }) {
      edges {
        node {
          name
          bio
          slug
          count {
            posts
          }
          socials {
            url
            icon
            label
          }
          profileImage {
            publicURL
            childImageSharp {
              fluid {
                ...GatsbyImageSharpFluid_withWebp_tracedSVG
              }
            }
          }
        }
      }
    }
    draftboxSettings {
      identity {
        siteName
      }
    }
  }
`;
